import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Truck, MapPin, Package, Clock, CheckCircle, XCircle, Camera, 
  Pen, ChevronRight, ChevronLeft, Phone, Navigation, AlertTriangle,
  Wifi, WifiOff, RefreshCw, User, Fuel, ClipboardCheck, ArrowLeft,
  Play, Check, X, Upload, Shield, CreditCard, Banknote, Loader2
} from 'lucide-react';
import api from '../services/api';

// IndexedDB helper for offline storage
const DB_NAME = 'tankerbase_driver';
const DB_VERSION = 1;

const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      
      // Store for deliveries
      if (!db.objectStoreNames.contains('deliveries')) {
        db.createObjectStore('deliveries', { keyPath: 'id' });
      }
      
      // Store for pending sync items
      if (!db.objectStoreNames.contains('pendingSync')) {
        db.createObjectStore('pendingSync', { keyPath: 'id', autoIncrement: true });
      }
      
      // Store for cached data
      if (!db.objectStoreNames.contains('cache')) {
        db.createObjectStore('cache', { keyPath: 'key' });
      }
    };
  });
};

const saveToIndexedDB = async (storeName, data) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.put(data);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
};

const getFromIndexedDB = async (storeName, key) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = key ? store.get(key) : store.getAll();
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
};

const deleteFromIndexedDB = async (storeName, key) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.delete(key);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
};

// Delivery status flow
const STATUS_FLOW = ['pending', 'arrived', 'in_progress', 'completed'];

const STATUS_LABELS = {
  pending: 'Pending',
  arrived: 'Arrived',
  in_progress: 'In Progress',
  completed: 'Completed',
  failed: 'Failed'
};

const STATUS_COLORS = {
  pending: 'bg-gray-100 text-gray-800',
  arrived: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-orange-100 text-orange-800',
  completed: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800'
};

const DriverApp = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [syncing, setSyncing] = useState(false);
  const [pendingSyncCount, setPendingSyncCount] = useState(0);
  
  // Data states
  const [driverData, setDriverData] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // UI states
  const [activeView, setActiveView] = useState('list'); // list, detail, safety, signature, camera, complete, payment
  const [safetyChecks, setSafetyChecks] = useState({});
  const [signatureData, setSignatureData] = useState(null);
  const [signerName, setSignerName] = useState('');
  const [photos, setPhotos] = useState([]);
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [deliveredQuantities, setDeliveredQuantities] = useState({});
  
  // Payment states
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [deliveryPayments, setDeliveryPayments] = useState([]);
  
  // Location tracking states
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [trackingActive, setTrackingActive] = useState(false);
  const locationWatchRef = useRef(null);
  
  // Signature canvas ref
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  
  // Monitor online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  
  // Start location tracking when app loads
  useEffect(() => {
    startLocationTracking();
    
    return () => {
      // Clean up location watcher
      if (locationWatchRef.current) {
        navigator.geolocation.clearWatch(locationWatchRef.current);
      }
    };
  }, []);
  
  // Send location to server every 10 seconds when online
  useEffect(() => {
    if (!isOnline || !currentLocation || !trackingActive) return;
    
    const sendLocation = async () => {
      try {
        const token = localStorage.getItem('token');
        const apiUrl = process.env.REACT_APP_BACKEND_URL;
        
        await fetch(`${apiUrl}/api/driver/location`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            latitude: currentLocation.latitude,
            longitude: currentLocation.longitude,
            accuracy: currentLocation.accuracy,
            heading: currentLocation.heading,
            speed: currentLocation.speed
          })
        });
      } catch (error) {
        console.error('Failed to send location:', error);
      }
    };
    
    // Send immediately when location changes
    sendLocation();
    
    // Also set up interval to send every 10 seconds
    const interval = setInterval(sendLocation, 10000);
    
    return () => clearInterval(interval);
  }, [currentLocation, isOnline, trackingActive]);
  
  // Start GPS location tracking
  const startLocationTracking = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      return;
    }
    
    setTrackingActive(true);
    
    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 5000
    };
    
    // Watch position for continuous updates
    locationWatchRef.current = navigator.geolocation.watchPosition(
      (position) => {
        setCurrentLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          heading: position.coords.heading,
          speed: position.coords.speed,
          timestamp: new Date().toISOString()
        });
        setLocationError(null);
      },
      (error) => {
        console.error('Location error:', error);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setLocationError('Location permission denied. Please enable location access.');
            break;
          case error.POSITION_UNAVAILABLE:
            setLocationError('Location information unavailable.');
            break;
          case error.TIMEOUT:
            setLocationError('Location request timed out.');
            break;
          default:
            setLocationError('An unknown error occurred.');
        }
        setTrackingActive(false);
      },
      options
    );
  };
  
  // Handle payment success/cancel redirects
  useEffect(() => {
    const paymentSuccess = searchParams.get('payment_success');
    const paymentCancelled = searchParams.get('payment_cancelled');
    const sessionId = searchParams.get('session_id');
    
    // Always reset processing state when page loads (in case we returned from payment)
    setProcessingPayment(false);
    
    if (paymentSuccess && sessionId) {
      // Poll for payment status
      pollPaymentStatus(sessionId);
      // Clear URL params
      window.history.replaceState({}, '', '/driver');
    } else if (paymentCancelled) {
      toast.error('Payment was cancelled');
      window.history.replaceState({}, '', '/driver');
    }
  }, [searchParams]);
  
  // Poll payment status after redirect from Stripe
  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out. Please check your payments.');
      return;
    }
    
    try {
      const response = await api.get(`/payments/status/${sessionId}`);
      const data = response.data;
      
      if (data.payment_status === 'paid') {
        toast.success(`Payment of £${data.amount?.toFixed(2)} received successfully!`);
        loadDriverData(); // Refresh deliveries
        return;
      } else if (data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        return;
      }
      
      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    }
  };
  
  // Load data on mount
  useEffect(() => {
    loadDriverData();
    checkPendingSync();
  }, []);
  
  // Auto-sync when coming online
  useEffect(() => {
    if (isOnline && pendingSyncCount > 0) {
      syncPendingData();
    }
  }, [isOnline]);
  
  const checkPendingSync = async () => {
    try {
      const pending = await getFromIndexedDB('pendingSync');
      setPendingSyncCount(pending?.length || 0);
    } catch (e) {
      console.log('Error checking pending sync:', e);
    }
  };
  
  const loadDriverData = async () => {
    setLoading(true);
    
    try {
      if (isOnline) {
        // Fetch from API
        const response = await api.get('/driver/today');
        setDriverData(response.data);
        setDeliveries(response.data.deliveries || []);
        
        // Cache for offline use
        await saveToIndexedDB('cache', { key: 'driverData', data: response.data, timestamp: Date.now() });
        for (const delivery of response.data.deliveries || []) {
          await saveToIndexedDB('deliveries', delivery);
        }
      } else {
        // Load from cache
        const cached = await getFromIndexedDB('cache', 'driverData');
        if (cached) {
          setDriverData(cached.data);
          const cachedDeliveries = await getFromIndexedDB('deliveries');
          setDeliveries(cachedDeliveries || []);
        }
      }
    } catch (error) {
      console.error('Error loading driver data:', error);
      // Try to load from cache on error
      try {
        const cached = await getFromIndexedDB('cache', 'driverData');
        if (cached) {
          setDriverData(cached.data);
          const cachedDeliveries = await getFromIndexedDB('deliveries');
          setDeliveries(cachedDeliveries || []);
        }
      } catch (e) {
        console.log('No cached data available');
      }
    }
    
    setLoading(false);
  };
  
  const syncPendingData = async () => {
    if (!isOnline || syncing) return;
    
    setSyncing(true);
    
    try {
      const pending = await getFromIndexedDB('pendingSync');
      
      for (const item of pending || []) {
        try {
          await api.post('/driver/sync', [item.data]);
          await deleteFromIndexedDB('pendingSync', item.id);
        } catch (e) {
          console.log('Failed to sync item:', e);
        }
      }
      
      await checkPendingSync();
      await loadDriverData();
    } catch (e) {
      console.error('Sync error:', e);
    }
    
    setSyncing(false);
  };
  
  const updateDeliveryStatus = async (deliveryId, status, additionalData = {}) => {
    // Update local state
    setDeliveries(prev => prev.map(d => 
      d.id === deliveryId ? { ...d, status, ...additionalData } : d
    ));
    
    if (selectedDelivery?.id === deliveryId) {
      setSelectedDelivery(prev => ({ ...prev, status, ...additionalData }));
    }
    
    // Update IndexedDB
    const delivery = deliveries.find(d => d.id === deliveryId);
    if (delivery) {
      await saveToIndexedDB('deliveries', { ...delivery, status, ...additionalData });
    }
    
    // Try to sync or queue for later
    if (isOnline) {
      try {
        await api.post(`/driver/delivery/${deliveryId}/update-status`, { status });
      } catch (e) {
        // Queue for later sync
        await saveToIndexedDB('pendingSync', {
          data: {
            delivery_id: deliveryId,
            status,
            ...additionalData,
            sync_timestamp: new Date().toISOString()
          }
        });
        await checkPendingSync();
      }
    } else {
      // Queue for later sync
      await saveToIndexedDB('pendingSync', {
        data: {
          delivery_id: deliveryId,
          status,
          ...additionalData,
          sync_timestamp: new Date().toISOString()
        }
      });
      await checkPendingSync();
    }
  };
  
  const selectDelivery = async (delivery) => {
    setSelectedDelivery(delivery);
    setActiveView('detail');
    
    // Initialize delivered quantities from order lines
    const quantities = {};
    (delivery.order_lines || delivery.lines || []).forEach(line => {
      quantities[line.product_id] = {
        quantity: line.quantity || 0,
        meter_start: '',
        meter_end: ''
      };
    });
    setDeliveredQuantities(quantities);
    
    // Calculate total amount for payment
    const total = (delivery.order_lines || delivery.lines || []).reduce((sum, line) => {
      return sum + (line.quantity || 0) * (line.unit_price || 0);
    }, 0);
    setPaymentAmount(total.toFixed(2));
    
    // Load existing payments for this delivery
    if (isOnline) {
      try {
        const response = await api.get(`/payments/delivery/${delivery.id}`);
        setDeliveryPayments(response.data || []);
      } catch (e) {
        console.log('Failed to load payments:', e);
        setDeliveryPayments([]);
      }
    }
  };
  
  // Initiate card payment via Stripe or BOIPA
  const initiateCardPayment = async (provider = 'stripe') => {
    if (!selectedDelivery || !paymentAmount || processingPayment) {
      return;
    }
    
    setProcessingPayment(true);
    toast.info(`Processing ${provider.toUpperCase()} payment...`);
    
    const paymentPayload = {
      delivery_id: selectedDelivery.id,
      amount: parseFloat(paymentAmount),
      origin_url: window.location.origin,
      provider: provider
    };
    
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${apiUrl}/api/payments/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(paymentPayload)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Server error ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.checkout_url) {
        toast.success('Redirecting to payment page...');
        window.location.href = data.checkout_url;
      } else {
        toast.error('Failed to create payment session');
        setProcessingPayment(false);
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.message || 'Payment failed. Please try again.');
      setProcessingPayment(false);
    }
  };
  
  const handleArrived = () => {
    updateDeliveryStatus(selectedDelivery.id, 'arrived', { 
      arrived_at: new Date().toISOString() 
    });
    setActiveView('safety');
  };
  
  const handleSafetyComplete = async () => {
    // Save safety checks
    if (isOnline) {
      try {
        const checks = Object.entries(safetyChecks).map(([id, checked]) => ({
          id,
          checked,
          timestamp: new Date().toISOString()
        }));
        await api.post(`/deliveries/${selectedDelivery.id}/safety-check`, checks);
      } catch (e) {
        console.log('Failed to save safety checks:', e);
      }
    }
    
    updateDeliveryStatus(selectedDelivery.id, 'in_progress');
    setActiveView('detail');
  };
  
  // Signature canvas handlers
  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
  }, []);
  
  useEffect(() => {
    if (activeView === 'signature') {
      initCanvas();
    }
  }, [activeView, initCanvas]);
  
  const getCanvasCoords = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height)
    };
  };
  
  const startDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getCanvasCoords(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  
  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getCanvasCoords(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  
  const stopDrawing = () => {
    setIsDrawing(false);
  };
  
  const clearSignature = () => {
    initCanvas();
    setSignatureData(null);
  };
  
  const saveSignature = async () => {
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL('image/png');
    setSignatureData(dataUrl);
    
    // Save to delivery
    if (isOnline) {
      try {
        await api.post(`/driver/delivery/${selectedDelivery.id}/signature`, {
          signer_name: signerName,
          signature_data: dataUrl
        });
      } catch (e) {
        console.log('Failed to save signature online:', e);
      }
    }
    
    // Update local state
    setSelectedDelivery(prev => ({
      ...prev,
      signature: { signer_name: signerName, data_url: dataUrl, captured_at: new Date().toISOString() }
    }));
    
    // Save to IndexedDB
    await saveToIndexedDB('deliveries', {
      ...selectedDelivery,
      signature: { signer_name: signerName, data_url: dataUrl, captured_at: new Date().toISOString() }
    });
    
    setActiveView('complete');
  };
  
  // Camera handler
  const handlePhotoCapture = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = async (event) => {
      const photoData = event.target.result;
      const newPhoto = {
        id: Date.now().toString(),
        data_url: photoData,
        type: 'delivery',
        captured_at: new Date().toISOString()
      };
      
      setPhotos(prev => [...prev, newPhoto]);
      
      // Try to upload
      if (isOnline) {
        try {
          await api.post(`/driver/delivery/${selectedDelivery.id}/photo`, {
            photo_data: photoData,
            photo_type: 'delivery'
          });
        } catch (e) {
          console.log('Failed to upload photo:', e);
        }
      }
    };
    reader.readAsDataURL(file);
  };
  
  const completeDelivery = async () => {
    const quantities = Object.entries(deliveredQuantities).map(([product_id, data]) => ({
      product_id,
      quantity: data.quantity,
      meter_start: data.meter_start,
      meter_end: data.meter_end
    }));
    
    if (isOnline) {
      try {
        await api.post(`/driver/delivery/${selectedDelivery.id}/complete`, {
          delivered_quantities: quantities,
          notes: deliveryNotes
        });
      } catch (e) {
        console.log('Failed to complete online:', e);
        // Queue for sync
        await saveToIndexedDB('pendingSync', {
          data: {
            delivery_id: selectedDelivery.id,
            status: 'completed',
            delivered_quantities: quantities,
            notes: deliveryNotes,
            signature: selectedDelivery.signature,
            photos: photos,
            completed_at: new Date().toISOString(),
            sync_timestamp: new Date().toISOString()
          }
        });
        await checkPendingSync();
      }
    } else {
      await saveToIndexedDB('pendingSync', {
        data: {
          delivery_id: selectedDelivery.id,
          status: 'completed',
          delivered_quantities: quantities,
          notes: deliveryNotes,
          signature: selectedDelivery.signature,
          photos: photos,
          completed_at: new Date().toISOString(),
          sync_timestamp: new Date().toISOString()
        }
      });
      await checkPendingSync();
    }
    
    updateDeliveryStatus(selectedDelivery.id, 'completed', {
      completed_at: new Date().toISOString()
    });
    
    // Reset and go back to list
    setSelectedDelivery(null);
    setSignatureData(null);
    setSignerName('');
    setPhotos([]);
    setDeliveryNotes('');
    setSafetyChecks({});
    setActiveView('list');
    
    // Refresh data
    loadDriverData();
  };
  
  // Render Loading
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-600">Loading your deliveries...</p>
        </div>
      </div>
    );
  }

  // Render Safety Checklist View
  if (activeView === 'safety') {
    const safetyItems = [
      { id: 'ppe', label: 'PPE worn (Hi-vis, gloves, safety boots)', required: true },
      { id: 'vehicle_check', label: 'Vehicle walk-around check completed', required: true },
      { id: 'hazards', label: 'Site hazards assessed', required: true },
      { id: 'spillkit', label: 'Spill kit available and accessible', required: true },
      { id: 'fire_extinguisher', label: 'Fire extinguisher checked', required: true },
      { id: 'tank_access', label: 'Tank access clear and safe', required: true },
      { id: 'grounding', label: 'Grounding/bonding equipment ready', required: true },
      { id: 'customer_present', label: 'Customer/representative present', required: false },
      { id: 'delivery_note', label: 'Delivery note prepared', required: true }
    ];
    
    const requiredChecks = safetyItems.filter(i => i.required);
    const allRequiredChecked = requiredChecks.every(item => safetyChecks[item.id]);
    
    return (
      <div className="min-h-screen bg-gray-100 pb-24">
        <div className="bg-navy-900 text-white p-4 sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => setActiveView('detail')} className="text-white hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold">Safety Checklist</h1>
              <p className="text-sm opacity-75">Complete before starting delivery</p>
            </div>
          </div>
        </div>
        
        <div className="p-4 space-y-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Shield className="w-5 h-5 text-orange-500" />
                Pre-Delivery Safety Checks
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {safetyItems.map(item => (
                <label key={item.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer">
                  <Checkbox 
                    checked={safetyChecks[item.id] || false}
                    onCheckedChange={(checked) => setSafetyChecks(prev => ({ ...prev, [item.id]: checked }))}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <span className="text-sm">{item.label}</span>
                    {item.required && <span className="text-red-500 text-xs ml-1">*</span>}
                  </div>
                </label>
              ))}
            </CardContent>
          </Card>
          
          <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t">
            <Button 
              className="w-full bg-orange-500 hover:bg-orange-600" 
              size="lg"
              disabled={!allRequiredChecked}
              onClick={handleSafetyComplete}
            >
              <Play className="w-5 h-5 mr-2" />
              Start Delivery
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  // Render Signature View
  if (activeView === 'signature') {
    return (
      <div className="min-h-screen bg-gray-100 pb-24">
        <div className="bg-navy-900 text-white p-4 sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => setActiveView('detail')} className="text-white hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold">Customer Signature</h1>
              <p className="text-sm opacity-75">Capture proof of delivery</p>
            </div>
          </div>
        </div>
        
        <div className="p-4 space-y-4">
          <Card>
            <CardContent className="pt-4">
              <label className="block text-sm font-medium mb-2">Signer Name</label>
              <Input 
                placeholder="Enter customer name"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                className="mb-4"
              />
              
              <label className="block text-sm font-medium mb-2">Signature</label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg overflow-hidden bg-white">
                <canvas
                  ref={canvasRef}
                  width={350}
                  height={200}
                  className="w-full touch-none"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />
              </div>
              <p className="text-xs text-gray-500 mt-2 text-center">Sign in the box above</p>
              
              <Button variant="outline" className="w-full mt-3" onClick={clearSignature}>
                Clear Signature
              </Button>
            </CardContent>
          </Card>
          
          <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t">
            <Button 
              className="w-full bg-orange-500 hover:bg-orange-600" 
              size="lg"
              disabled={!signerName.trim()}
              onClick={saveSignature}
            >
              <Check className="w-5 h-5 mr-2" />
              Confirm Signature
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  // Render Complete View
  if (activeView === 'complete') {
    const lines = selectedDelivery?.order_lines || selectedDelivery?.lines || [];
    
    return (
      <div className="min-h-screen bg-gray-100 pb-24">
        <div className="bg-navy-900 text-white p-4 sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => setActiveView('detail')} className="text-white hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold">Complete Delivery</h1>
              <p className="text-sm opacity-75">Enter delivered quantities</p>
            </div>
          </div>
        </div>
        
        <div className="p-4 space-y-4">
          {/* Signature Confirmed */}
          <Card className="border-green-200 bg-green-50">
            <CardContent className="pt-4 flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <div>
                <p className="font-medium text-green-800">Signature Captured</p>
                <p className="text-sm text-green-600">Signed by: {signerName}</p>
              </div>
            </CardContent>
          </Card>
          
          {/* Delivered Quantities */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Delivered Quantities</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {lines.map((line, idx) => (
                <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                  <p className="font-medium text-sm mb-2">{line.product_name}</p>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-xs text-gray-500">Quantity (L)</label>
                      <Input 
                        type="number"
                        value={deliveredQuantities[line.product_id]?.quantity || line.quantity}
                        onChange={(e) => setDeliveredQuantities(prev => ({
                          ...prev,
                          [line.product_id]: { ...prev[line.product_id], quantity: e.target.value }
                        }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Meter Start</label>
                      <Input 
                        type="number"
                        placeholder="0"
                        value={deliveredQuantities[line.product_id]?.meter_start || ''}
                        onChange={(e) => setDeliveredQuantities(prev => ({
                          ...prev,
                          [line.product_id]: { ...prev[line.product_id], meter_start: e.target.value }
                        }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Meter End</label>
                      <Input 
                        type="number"
                        placeholder="0"
                        value={deliveredQuantities[line.product_id]?.meter_end || ''}
                        onChange={(e) => setDeliveredQuantities(prev => ({
                          ...prev,
                          [line.product_id]: { ...prev[line.product_id], meter_end: e.target.value }
                        }))}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
          
          {/* Photos */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Camera className="w-4 h-4" />
                Photos ({photos.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 flex-wrap mb-3">
                {photos.map((photo, idx) => (
                  <div key={idx} className="w-16 h-16 rounded overflow-hidden">
                    <img src={photo.data_url} alt="" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
              <label className="cursor-pointer">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:bg-gray-50">
                  <Camera className="w-6 h-6 mx-auto text-gray-400 mb-1" />
                  <span className="text-sm text-gray-500">Add Photo</span>
                </div>
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handlePhotoCapture} />
              </label>
            </CardContent>
          </Card>
          
          {/* Notes */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Delivery Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea 
                placeholder="Any comments about the delivery..."
                value={deliveryNotes}
                onChange={(e) => setDeliveryNotes(e.target.value)}
                rows={3}
              />
            </CardContent>
          </Card>
          
          <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t">
            <Button 
              className="w-full bg-green-600 hover:bg-green-700" 
              size="lg"
              onClick={completeDelivery}
            >
              <CheckCircle className="w-5 h-5 mr-2" />
              Complete Delivery
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  // Render Delivery Detail View
  if (activeView === 'detail' && selectedDelivery) {
    const delivery = selectedDelivery;
    const canArrive = delivery.status === 'pending';
    const canStartDelivery = delivery.status === 'arrived';
    const canGetSignature = delivery.status === 'in_progress' && !delivery.signature;
    const canComplete = delivery.status === 'in_progress' && delivery.signature;
    const isCompleted = delivery.status === 'completed';
    
    return (
      <div className="min-h-screen bg-gray-100 pb-24">
        {/* Header */}
        <div className="bg-navy-900 text-white p-4 sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => setActiveView('list')} className="text-white hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-lg font-bold">{delivery.customer_name}</h1>
              <p className="text-sm opacity-75">{delivery.order_number}</p>
            </div>
            <Badge className={STATUS_COLORS[delivery.status]}>{STATUS_LABELS[delivery.status]}</Badge>
          </div>
        </div>
        
        <div className="p-4 space-y-4">
          {/* Delivery Address */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-orange-500 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium">{delivery.site_name}</p>
                  <p className="text-sm text-gray-600">{delivery.site_address}</p>
                  {delivery.delivery_instructions && (
                    <p className="text-sm text-blue-600 mt-2 p-2 bg-blue-50 rounded">
                      <strong>Instructions:</strong> {delivery.delivery_instructions}
                    </p>
                  )}
                </div>
                {delivery.customer_phone && (
                  <a href={`tel:${delivery.customer_phone}`} className="p-2 bg-green-100 rounded-full">
                    <Phone className="w-5 h-5 text-green-600" />
                  </a>
                )}
              </div>
            </CardContent>
          </Card>
          
          {/* Order Lines */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Fuel className="w-4 h-4 text-orange-500" />
                Products to Deliver
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(delivery.order_lines || delivery.lines || []).map((line, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div>
                    <p className="font-medium text-sm">{line.product_name}</p>
                    <p className="text-xs text-gray-500">Unit price: £{line.unit_price?.toFixed(2)}</p>
                  </div>
                  <p className="font-bold text-orange-600">{line.quantity?.toLocaleString()} L</p>
                </div>
              ))}
            </CardContent>
          </Card>
          
          {/* Signature Status */}
          {delivery.signature && (
            <Card className="border-green-200 bg-green-50">
              <CardContent className="pt-4 flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-green-600" />
                <div>
                  <p className="font-medium text-green-800">Signature Captured</p>
                  <p className="text-sm text-green-600">Signed by: {delivery.signature.signer_name}</p>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Payment Status */}
          {delivery.payment_collected && (
            <Card className="border-green-200 bg-green-50">
              <CardContent className="pt-4 flex items-center gap-3">
                <Banknote className="w-6 h-6 text-green-600" />
                <div>
                  <p className="font-medium text-green-800">Payment Collected</p>
                  <p className="text-sm text-green-600">£{delivery.payment_amount?.toFixed(2)} by card</p>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Payment History */}
          {deliveryPayments.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-green-600" />
                  Payments
                </CardTitle>
              </CardHeader>
              <CardContent>
                {deliveryPayments.map((payment, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                    <div>
                      <p className="font-medium text-sm">£{payment.amount?.toFixed(2)}</p>
                      <p className="text-xs text-gray-500">{new Date(payment.created_at).toLocaleString()}</p>
                    </div>
                    <Badge className={payment.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                      {payment.payment_status}
                    </Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          
          {/* Take Payment Card - Show for in_progress or completed deliveries without payment */}
          {(delivery.status === 'in_progress' || delivery.status === 'completed') && !delivery.payment_collected && isOnline && (
            <Card className="border-blue-200 bg-blue-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-blue-600" />
                  Take Card Payment
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-gray-500">Amount (£)</label>
                    <Input 
                      type="number"
                      step="0.01"
                      value={paymentAmount}
                      onChange={(e) => setPaymentAmount(e.target.value)}
                      placeholder="0.00"
                      className="mt-1"
                    />
                  </div>
                  
                  {/* Payment Provider Choice */}
                  <div className="space-y-2">
                    <label className="text-xs text-gray-500">Payment Provider</label>
                    
                    {/* Stripe Option */}
                    <Button 
                      className="w-full bg-[#635BFF] hover:bg-[#5851DB]" 
                      onClick={() => initiateCardPayment('stripe')}
                      disabled={processingPayment || !paymentAmount || parseFloat(paymentAmount) <= 0}
                    >
                      {processingPayment ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
                          </svg>
                          Pay with Stripe £{parseFloat(paymentAmount || 0).toFixed(2)}
                        </>
                      )}
                    </Button>
                    
                    {/* BOIPA Option */}
                    <Button 
                      variant="outline"
                      className="w-full border-[#00A86B] text-[#00A86B] hover:bg-[#00A86B]/10" 
                      onClick={() => initiateCardPayment('boipa')}
                      disabled={processingPayment || !paymentAmount || parseFloat(paymentAmount) <= 0}
                    >
                      {processingPayment ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                          </svg>
                          Pay with BOIPA £{parseFloat(paymentAmount || 0).toFixed(2)}
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-center text-muted-foreground">
                      BOIPA: Bank of Ireland Payment Acceptance
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Actions */}
          <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t space-y-2">
            {canArrive && (
              <Button className="w-full bg-blue-600 hover:bg-blue-700" size="lg" onClick={handleArrived}>
                <MapPin className="w-5 h-5 mr-2" />
                I've Arrived
              </Button>
            )}
            
            {canStartDelivery && (
              <Button className="w-full bg-orange-500 hover:bg-orange-600" size="lg" onClick={() => setActiveView('safety')}>
                <ClipboardCheck className="w-5 h-5 mr-2" />
                Safety Check & Start
              </Button>
            )}
            
            {canGetSignature && (
              <>
                <label className="cursor-pointer block">
                  <Button variant="outline" className="w-full" size="lg">
                    <Camera className="w-5 h-5 mr-2" />
                    Take Photo
                  </Button>
                  <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handlePhotoCapture} />
                </label>
                <Button className="w-full bg-orange-500 hover:bg-orange-600" size="lg" onClick={() => setActiveView('signature')}>
                  <Pen className="w-5 h-5 mr-2" />
                  Get Customer Signature
                </Button>
              </>
            )}
            
            {canComplete && (
              <Button className="w-full bg-green-600 hover:bg-green-700" size="lg" onClick={() => setActiveView('complete')}>
                <CheckCircle className="w-5 h-5 mr-2" />
                Complete Delivery
              </Button>
            )}
            
            {isCompleted && (
              <div className="text-center py-3 bg-green-100 rounded-lg">
                <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-1" />
                <p className="text-green-800 font-medium">Delivery Completed</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
  
  // Render Delivery List View (default)
  return (
    <div className="min-h-screen bg-gray-100 pb-4" data-testid="driver-app">
      {/* Header */}
      <div className="bg-navy-900 text-white p-4 sticky top-0 z-10">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center">
              <User className="w-5 h-5" />
            </div>
            <div>
              <h1 className="font-bold">{driverData?.driver_name || 'Driver'}</h1>
              <p className="text-xs opacity-75">{driverData?.vehicle?.registration || 'No vehicle assigned'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* GPS Status */}
            {trackingActive && currentLocation ? (
              <div className="flex items-center gap-1 text-green-400 text-xs" title={`Accuracy: ${currentLocation.accuracy?.toFixed(0)}m`}>
                <Navigation className="w-4 h-4" />
                GPS
              </div>
            ) : locationError ? (
              <div className="flex items-center gap-1 text-red-400 text-xs" title={locationError}>
                <Navigation className="w-4 h-4" />
                No GPS
              </div>
            ) : (
              <div className="flex items-center gap-1 text-amber-400 text-xs animate-pulse">
                <Navigation className="w-4 h-4" />
                ...
              </div>
            )}
            
            {/* Network Status */}
            {isOnline ? (
              <div className="flex items-center gap-1 text-green-400 text-xs">
                <Wifi className="w-4 h-4" />
                Online
              </div>
            ) : (
              <div className="flex items-center gap-1 text-amber-400 text-xs">
                <WifiOff className="w-4 h-4" />
                Offline
              </div>
            )}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={loadDriverData}
              className="text-white hover:bg-white/10"
            >
              <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
        
        {/* Sync Status */}
        {pendingSyncCount > 0 && (
          <div className="bg-amber-500/20 rounded-lg p-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Upload className="w-4 h-4" />
              <span className="text-sm">{pendingSyncCount} pending sync</span>
            </div>
            {isOnline && (
              <Button variant="ghost" size="sm" onClick={syncPendingData} className="text-white h-7 text-xs">
                Sync Now
              </Button>
            )}
          </div>
        )}
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 p-4">
        <Card className="p-3 text-center">
          <p className="text-2xl font-bold text-navy-900">{deliveries.length}</p>
          <p className="text-xs text-gray-500">Deliveries</p>
        </Card>
        <Card className="p-3 text-center">
          <p className="text-2xl font-bold text-green-600">
            {deliveries.filter(d => d.status === 'completed').length}
          </p>
          <p className="text-xs text-gray-500">Completed</p>
        </Card>
        <Card className="p-3 text-center">
          <p className="text-2xl font-bold text-orange-500">
            {deliveries.filter(d => d.status !== 'completed').length}
          </p>
          <p className="text-xs text-gray-500">Remaining</p>
        </Card>
      </div>
      
      {/* Current Location Card */}
      {currentLocation && (
        <div className="px-4 mb-3">
          <Card className="overflow-hidden">
            <CardHeader className="py-2 px-3 bg-slate-50">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Navigation className="w-4 h-4 text-orange-500" />
                  Your Location
                </CardTitle>
                <Badge variant="outline" className="text-xs">
                  {currentLocation.accuracy ? `±${currentLocation.accuracy.toFixed(0)}m` : 'GPS'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="h-32 bg-slate-100 relative">
                {/* Simple static map using Google Static Maps API */}
                <img 
                  src={`https://maps.googleapis.com/maps/api/staticmap?center=${currentLocation.latitude},${currentLocation.longitude}&zoom=15&size=400x150&scale=2&markers=color:orange%7C${currentLocation.latitude},${currentLocation.longitude}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`}
                  alt="Your location"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="hidden absolute inset-0 items-center justify-center bg-slate-100">
                  <div className="text-center text-sm text-gray-500">
                    <MapPin className="w-6 h-6 mx-auto mb-1 text-orange-500" />
                    <p>{currentLocation.latitude.toFixed(5)}, {currentLocation.longitude.toFixed(5)}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Location Error */}
      {locationError && (
        <div className="px-4 mb-3">
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              <p className="text-sm text-amber-700">{locationError}</p>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={startLocationTracking}
                className="ml-auto text-amber-700 hover:bg-amber-100"
              >
                Retry
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Delivery List */}
      <div className="px-4 space-y-3">
        <h2 className="font-semibold text-gray-700">Today's Deliveries</h2>
        
        {deliveries.length === 0 ? (
          <Card className="p-8 text-center">
            <Truck className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">No deliveries assigned for today</p>
          </Card>
        ) : (
          deliveries.map((delivery, idx) => (
            <Card 
              key={delivery.id || idx}
              className={`cursor-pointer hover:border-orange-500/50 transition-all ${
                delivery.status === 'completed' ? 'opacity-60' : ''
              }`}
              onClick={() => selectDelivery(delivery)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-mono text-gray-500">#{idx + 1}</span>
                      <Badge className={`${STATUS_COLORS[delivery.status]} text-xs`}>
                        {STATUS_LABELS[delivery.status]}
                      </Badge>
                    </div>
                    <p className="font-semibold truncate">{delivery.customer_name}</p>
                    <p className="text-sm text-gray-500 truncate">{delivery.site_address}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Package className="w-3 h-3" />
                        {(delivery.order_lines || delivery.lines || []).reduce((sum, l) => sum + (l.quantity || 0), 0).toLocaleString()} L
                      </span>
                      {delivery.order_number && (
                        <span>{delivery.order_number}</span>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default DriverApp;
